import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';

const PlayerContainer = styled.div`
  padding: 20px;
  background: linear-gradient(to bottom, rgba(247, 250, 252, 0.8), rgba(237, 242, 247, 0.8));
  border-radius: 0 0 12px 12px;
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PlayButton = styled(motion.button)`
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: none;
  background: ${props => props.disabled 
    ? 'linear-gradient(145deg, #d9e1e9, #e2e8f0)' 
    : 'linear-gradient(145deg, #3d4a5c, #4a5568)'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  box-shadow: ${props => props.disabled 
    ? '0 2px 8px rgba(0, 0, 0, 0.1)' 
    : '0 4px 10px rgba(0, 0, 0, 0.2)'};
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
  }
`;

const SkipButton = styled(motion.button)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(145deg, #e9ecef, #f1f5f9);
  color: #4a5568;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.4);
  }
`;

const VolumeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
`;

const VolumeIcon = styled(motion.button)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #4a5568;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:focus {
    outline: none;
  }
`;

const VolumeSlider = styled.input`
  -webkit-appearance: none;
  width: 60px;
  height: 4px;
  border-radius: 2px;
  background: #cbd5e0;
  outline: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #4a5568;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #4a5568;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
  }
  
  &::-webkit-slider-thumb:hover {
    transform: scale(1.2);
    background: #3182ce;
  }
  
  &::-moz-range-thumb:hover {
    transform: scale(1.2);
    background: #3182ce;
  }
`;

const LoadingSpinner = styled(motion.div)`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  class-name: loading-spinner;
`;

const SpeedSelector = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #cbd5e0;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  }
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  margin-bottom: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.08);
  
  &:hover {
    height: 10px;
    margin-top: -1px;
    margin-bottom: 9px;
  }
`;

const ProgressBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #3182ce, #4299e1);
  border-radius: 4px;
  width: ${props => `${props.progress}%`};
  transition: width 0.1s linear;
`;

const LoadingBar = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #4299e1 30%, #63b3ed 50%, #4299e1 70%);
  background-size: 200% 100%;
  border-radius: 4px;
  width: 100%;
  class-name: loading-bar;
`;

const ProgressHandle = styled(motion.div)`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 50%;
  left: ${props => `calc(${props.progress}% - 8px)`};
  transform: translateY(-50%);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  border: 2px solid #2b6cb0;
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #4a5568;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  padding: 16px;
  text-align: center;
  font-size: 14px;
  background: #fff5f5;
  border-radius: 8px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

// SVG icons for buttons
const PlayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);

const VolumeHighIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

const VolumeMuteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
  </svg>
);

export const AudioPlayer = ({ audioUrl, status, setStatus, onInitialPlay, error, autoScroll, autoPlay = true }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [playerError, setPlayerError] = useState(null);
  const [isAutoPlayReady, setIsAutoPlayReady] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [prevVolume, setPrevVolume] = useState(1);
  
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  
  // Initialize audio object when URL is available
  useEffect(() => {
    if (audioUrl) {
      if (!audioRef.current) {
        audioRef.current = new Audio();
        
        // Add explicit MIME type and caching
        audioRef.current.type = 'audio/mpeg';
        audioRef.current.preload = 'auto';
        
        audioRef.current.addEventListener('loadedmetadata', () => {
          console.log('Audio metadata loaded successfully');
          setDuration(audioRef.current.duration);
        });
        
        audioRef.current.addEventListener('timeupdate', () => {
          setCurrentTime(audioRef.current.currentTime);
        });
        
        audioRef.current.addEventListener('ended', () => {
          setIsPlaying(false);
          setStatus('ready');
        });
        
        audioRef.current.addEventListener('error', (e) => {
          console.error('Audio loading error:', e);
          console.error('Error code:', audioRef.current.error?.code);
          console.error('Error message:', audioRef.current.error?.message);
          
          // Try with a fetch request to see what's going wrong
          fetch(audioUrl)
            .then(response => {
              if (!response.ok) {
                throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
              }
              return response.text();
            })
            .then(content => {
              console.log('Audio content preview:', content.substring(0, 100));
              setPlayerError(`Audio format error. Please try a different browser.`);
            })
            .catch(err => {
              console.error('Fetch error:', err);
              setPlayerError(`Audio error: ${err.message || audioRef.current.error?.message || 'Unknown error'}`);
            });
            
          setStatus('error');
        });
        
        // Add canplay event to detect when audio is ready
        audioRef.current.addEventListener('canplay', () => {
          console.log('Audio is ready to play');
          setIsAutoPlayReady(true);
        });
      }
      
      // Set crossOrigin for CORS requests
      audioRef.current.crossOrigin = 'anonymous';
      
      // Clear any previous errors
      setPlayerError(null);
      
      // Set source and load the audio
      audioRef.current.src = audioUrl;
      
      // Load the audio
      try {
        console.log(`Loading audio from URL: ${audioUrl}`);
        audioRef.current.load();
      } catch (loadError) {
        console.error('Error during audio.load():', loadError);
        setPlayerError(`Error loading audio: ${loadError.message}`);
        setStatus('error');
      }
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute('src');
        audioRef.current.load(); // Reset the audio player
      }
    };
  }, [audioUrl, setStatus]);
  
  // Handle playback rate changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Auto-play when audio is ready
  useEffect(() => {
    if (autoPlay && isAutoPlayReady && status === 'ready' && audioRef.current) {
      // Small delay to ensure UI updates first
      const autoPlayTimer = setTimeout(() => {
        audioRef.current.play()
          .then(() => {
            setIsPlaying(true);
            setStatus('playing');
            setIsAutoPlayReady(false); // Reset for next time
          })
          .catch(err => {
            console.error('Auto-play error:', err);
            // Auto-play might be blocked by browser policy
            console.log('Auto-play might be blocked by browser policy');
            setIsAutoPlayReady(false);
          });
      }, 500);
      
      return () => clearTimeout(autoPlayTimer);
    }
  }, [isAutoPlayReady, status, setStatus, autoPlay]);
  
  const togglePlayPause = () => {
    if (status === 'idle') {
      // First time play, need to fetch audio
      onInitialPlay();
      return;
    }
    
    if (status === 'loading') {
      return; // Do nothing while loading
    }
    
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        setStatus('ready');
      } else {
        setPlayerError(null); // Clear any previous errors
        
        audioRef.current.play()
          .then(() => {
            setIsPlaying(true);
            setStatus('playing');
          })
          .catch(err => {
            console.error('Playback error:', err);
            console.error('Error name:', err.name);
            console.error('Error message:', err.message);
            setPlayerError(err.message);
            setStatus('error');
          });
      }
    }
  };

  const skipForward = () => {
    if (!audioRef.current || status === 'idle' || status === 'loading' || status === 'error') return;
    
    const newTime = Math.min(audioRef.current.duration, currentTime + 10);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const skipBackward = () => {
    if (!audioRef.current || status === 'idle' || status === 'loading' || status === 'error') return;
    
    const newTime = Math.max(0, currentTime - 10);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleMute = () => {
    if (isMuted) {
      setIsMuted(false);
      setVolume(prevVolume);
    } else {
      setPrevVolume(volume);
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    } else if (newVolume === 0 && !isMuted) {
      setIsMuted(true);
    }
  };
  
  const handleProgressClick = (e) => {
    if (!audioRef.current || status === 'idle' || status === 'loading' || status === 'error') return;
    
    const progressBar = progressBarRef.current;
    const rect = progressBar.getBoundingClientRect();
    const clickPositionRatio = (e.clientX - rect.left) / rect.width;
    const newTime = clickPositionRatio * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };
  
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  const calculateProgress = () => {
    if (!duration) return 0;
    return (currentTime / duration) * 100;
  };
  
  if (status === 'error') {
    return (
      <ErrorMessage>
        {playerError || error || 'Failed to load audio. Please try again.'}
        <motion.button 
          onClick={() => {
            setPlayerError(null);
            onInitialPlay();
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{ 
            marginTop: '16px', 
            padding: '8px 16px', 
            backgroundColor: '#3182ce', 
            color: 'white', 
            border: 'none', 
            borderRadius: '6px',
            cursor: 'pointer',
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
            fontWeight: 500
          }}
        >
          Retry
        </motion.button>
      </ErrorMessage>
    );
  }
  
  return (
    <PlayerContainer>
      <ControlsContainer>
        <ControlsGroup>
          <SkipButton
            onClick={skipBackward}
            disabled={status === 'idle' || status === 'loading'}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Skip 10 seconds backward"
          >
            -10s
          </SkipButton>
          
          <PlayButton 
            onClick={togglePlayPause}
            disabled={status === 'loading'}
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.05, backgroundColor: '#2d3748' }}
            transition={{ type: 'spring', stiffness: 300, damping: 10 }}
            data-loading={status === 'loading' ? "true" : "false"}
          >
            <AnimatePresence mode="wait">
              {status === 'loading' ? (
                <LoadingSpinner
                  key="loading"
                  animate={{ rotate: 360 }}
                  transition={{ 
                    repeat: Infinity, 
                    duration: 1,
                    ease: 'linear'
                  }}
                  data-loading="true"
                />
              ) : isPlaying ? (
                <motion.div
                  key="pause"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <PauseIcon />
                </motion.div>
              ) : (
                <motion.div
                  key="play"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <PlayIcon />
                </motion.div>
              )}
            </AnimatePresence>
          </PlayButton>
          
          <SkipButton
            onClick={skipForward}
            disabled={status === 'idle' || status === 'loading'}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Skip 10 seconds forward"
          >
            +10s
          </SkipButton>
          
          <VolumeContainer>
            <VolumeIcon
              onClick={toggleMute}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {isMuted || volume === 0 ? <VolumeMuteIcon /> : <VolumeHighIcon />}
            </VolumeIcon>
            <VolumeSlider
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
            />
          </VolumeContainer>
        </ControlsGroup>
        
        <SpeedSelector 
          value={playbackRate}
          onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
          disabled={status === 'idle' || status === 'loading'}
        >
          <option value="0.5">0.5x</option>
          <option value="0.75">0.75x</option>
          <option value="1">1x</option>
          <option value="1.25">1.25x</option>
          <option value="1.5">1.5x</option>
          <option value="2">2x</option>
        </SpeedSelector>
      </ControlsContainer>
      
      <ProgressBarContainer 
        ref={progressBarRef} 
        onClick={handleProgressClick}
      >
        <AnimatePresence mode="wait">
          {status === 'loading' ? (
            <LoadingBar
              key="loading-bar"
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%']
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          ) : (
            <ProgressBar key="progress-bar" progress={calculateProgress()} />
          )}
        </AnimatePresence>
        
        {status !== 'idle' && status !== 'loading' && (
          <ProgressHandle 
            progress={calculateProgress()}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            whileHover={{ scale: 1.2 }}
            whileDrag={{ scale: 1.3 }}
          />
        )}
      </ProgressBarContainer>
      
      <TimeDisplay>
        <span>{formatTime(currentTime)}</span>
        <span>{duration ? formatTime(duration) : '--:--'}</span>
      </TimeDisplay>
    </PlayerContainer>
  );
}; 